import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Phone, Mail, Users,
  Command, FileSearch, Bot,
} from "lucide-react";
import { API } from "../config/api";
import { ADMIN_EMAILS } from "../config/staffConfig";
import MLCOQueueBadge from "./MLCOQueueBadge";
import TopNavAvatarMenu from "./TopNavAvatarMenu";

const BONSAI_MAIL_URL = "https://bonsai-caseminder-portal.online/bonsai";

// Brief 51.1 (14 Jul 2026) — chip row trimmed. Director-only surfaces
// (App Settings, System Status, Directors Lounge, Reports) collapsed into
// the avatar dropdown (``TopNavAvatarMenu``) so non-directors see 6
// chips + avatar and directors see the same 6 chips + a 4-item avatar
// submenu. The Help chip is gone entirely — Mate is the sole helper.
// Every chip below is visible to all authenticated staff.
// 26 Oct 2026 refresh: each chip has its own hue (see .cm-chip in index.css);
// Mate, the one helper, is the gradient pill.
const CHIPS = [
  { label: "Call note", icon: Phone, hue: "#38bdf8", action: "event", event: "open-call-note", title: "Telephone attendance note or 'while you were out' message" },
  { label: "Bonsai Mail", icon: Mail, hue: "#60a5fa", action: "external", url: BONSAI_MAIL_URL },
  { label: "Contacts", icon: Users, hue: "#a78bfa", action: "navigate", path: "/contacts", title: "Browse the firm's contacts directory" },
  { label: "Find document", icon: FileSearch, hue: "#22d3ee", action: "navigate", path: "/documents/find", title: "Search documents across SharePoint matter folders" },
  // BM-H620 — Bonsai Agents. Gated on the live backend flag, same as the
  // Sidebar entry, so it never links to a page that 404s while it's off.
  {
    label: "Bonsai Agents", icon: Bot, hue: "#c084fc", action: "navigate", path: "/bonsai-agents",
    title: "Start, watch and collect a specialist agent review",
    visible: (ctx) => !!ctx?.reviewsPageEnabled,
  },
  { label: "Quick actions ⌘K", icon: Command, hue: "#818cf8", action: "event", event: "open-command-palette" },
  { label: "Mate", icon: Sparkles, hue: "#e879f9", featured: true, action: "event", event: "open-bonsai-mate", title: "Bonsai Mate — chat, ask, search and get things done" },
  // BM-H4115 (9 Oct 2026): the separate "Assistant" chip was folded into
  // Mate, so staff have one helper. Mate answers from the SOP too.
];

export default function TopNavBar() {
  const navigate = useNavigate();
  // Brief 14 PR2 — show MLCO badge only for users wearing the MLCO/COLP hat
  // or directors. Brief 35 — also drive director-only chip gating off the
  // same single fetch (don't fire /user-role twice). Soft-fails to
  // false for both flags.
  const [showMlcoBadge, setShowMlcoBadge] = useState(false);
  const [isDirector, setIsDirector] = useState(false);
  const [reviewsPageEnabled, setReviewsPageEnabled] = useState(false);

  // BM-H620 — safe default off, same contract as the Sidebar's fetch: a
  // failed read just keeps the chip hidden.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tenant/features`);
        if (!cancelled) setReviewsPageEnabled(!!res.data?.reviews_page_enabled);
      } catch {
        if (!cancelled) setReviewsPageEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const currentUserEmail =
    typeof window !== "undefined"
      ? sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || ""
      : "";

  useEffect(() => {
    if (!currentUserEmail) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/user-role`, {
          headers: { "X-Bonsai-User-Id": currentUserEmail },
        });
        if (cancelled) return;
        setShowMlcoBadge(!!(res.data?.is_mlco || res.data?.is_colp || res.data?.is_director));
        setIsDirector(!!res.data?.is_director);
      } catch {
        if (!cancelled) {
          setShowMlcoBadge(false);
          setIsDirector(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentUserEmail]);
  const isAdmin = ADMIN_EMAILS.has(currentUserEmail);

  const handleClick = (chip) => {
    switch (chip.action) {
      case "event":
        window.dispatchEvent(new CustomEvent(chip.event));
        break;
      case "external":
        window.open(chip.url, "_blank", "noopener,noreferrer");
        break;
      default:
        navigate(chip.path);
    }
  };

  return (
    <div className="cm-topbar relative flex items-center justify-end h-11 px-4 border-b border-slate-700/40 bg-navy/80 shrink-0 gap-2">
      {showMlcoBadge && <MLCOQueueBadge />}
      {CHIPS.filter((chip) => typeof chip.visible !== "function" || chip.visible({ reviewsPageEnabled })).map((chip) => {
        const Icon = chip.icon;
        return (
          <button
            key={chip.label}
            onClick={() => handleClick(chip)}
            title={chip.title}
            data-testid={`topnav-chip-${chip.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            style={{ "--c1": chip.hue }}
            className={`cm-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
              border transition duration-150 whitespace-nowrap ${chip.featured
                ? "cm-chip-featured text-white border-white/20"
                : "bg-slate-800 text-slate-300 border-slate-700/50 hover:bg-slate-700 hover:text-slate-100"}`}
          >
            <Icon size={14} className="shrink-0" />
            <span className="hidden sm:inline">{chip.label}</span>
          </button>
        );
      })}
      <TopNavAvatarMenu
        currentUserEmail={currentUserEmail}
        isDirector={isDirector}
        isAdmin={isAdmin}
      />
    </div>
  );
}
